import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Journey } from '../../types/journey';
import { JourneyApiService } from '../../services/journeyApiService';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BasePageLayout from '../../components/BasePageLayout';
import './JourneyListPage.css';

const JourneyListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadJourneys();
    }
  }, [user]);

  const loadJourneys = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userJourneys = await JourneyApiService.getUserAllJourneys();
      setJourneys(userJourneys);
    } catch (err: any) {
      console.error('Error loading journeys:', err);
      setError(err.message || 'Failed to load journeys');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (journey: Journey) => {
    const progress = (journey as any).progress;
    
    if (journey.isActive) {
      return <span className="journey-badge active">{t('journeyList.active')}</span>;
    } else if (progress && progress.percentage === 100) {
      return <span className="journey-badge completed">{t('journeyList.completed')}</span>;
    } else {
      return <span className="journey-badge inactive">{t('journeyList.inactive')}</span>;
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'Beginner':
        return '🌱';
      case 'Have Idea':
        return '💡';
      case 'In Progress':
        return '🚀';
      default:
        return '📈';
    }
  };

  const getMarketIcon = (market: string) => {
    switch (market) {
      case 'IT/Tech':
        return '💻';
      case 'E-commerce':
        return '🛒';
      case 'Social Media/Influencer':
        return '📱';
      case 'Other':
        return '🏢';
      default:
        return '🚀';
    }
  };

  const handleJourneyClick = (journeyId: string) => {
    navigate(`/journey/${journeyId}`);
  };

  const handleCreateNewJourney = () => {
    navigate('/startup-journey/setup');
  };

  if (!user) {
    return (
      <BasePageLayout maxWidth="none">
        <div className="journey-list-auth-required">
          <h2>{t('journeyList.authRequired')}</h2>
          <p>{t('journeyList.pleaseLogin')}</p>
        </div>
      </BasePageLayout>
    );
  }

  if (isLoading) {
    return (
      <BasePageLayout maxWidth="none">
        <div className="journey-list-loading">
          <div className="journey-list-spinner"></div>
          <p>{t('journeyList.loading')}</p>
        </div>
      </BasePageLayout>
    );
  }

  if (error) {
    return (
      <BasePageLayout maxWidth="none">
        <div className="journey-list-error">
          <h2>{t('journeyList.errorLoading')}</h2>
          <p>{error}</p>
          <button onClick={loadJourneys} className="journey-list-retry-button">
            {t('journeyList.tryAgain')}
          </button>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout maxWidth="none">
      <div className="journey-list">
        <div className="journey-list-header">
          <div className="journey-list-title">
            <h1>{t('journeyList.pageTitle')}</h1>
            <p>{t('journeyList.pageDescription')}</p>
          </div>
          <button 
            className="journey-list-create-button"
            onClick={handleCreateNewJourney}
          >
            <span className="create-icon">+</span>
            {t('journeyList.createNewJourney')}
          </button>
        </div>

        {journeys.length === 0 ? (
          <div className="journey-list-empty">
            <div className="journey-list-empty-icon">🚀</div>
            <h3>{t('journeyList.noJourneysYet')}</h3>
            <p>{t('journeyList.noJourneysDescription')}</p>
            <button 
              className="journey-list-empty-create-button"
              onClick={handleCreateNewJourney}
            >
              {t('journeyList.createFirstJourney')}
            </button>
          </div>
        ) : (
          <div className="journey-list-grid">
            {journeys.map((journey) => {
              const progress = (journey as any).progress;
              return (
                <div
                  key={journey.id}
                  className="journey-card"
                  onClick={() => handleJourneyClick(journey.id)}
                >
                  <div className="journey-card-header">
                    <div className="journey-card-info">
                      <div className="journey-card-stage">
                        <span className="stage-icon">{getStageIcon(journey.stage)}</span>
                        <span className="stage-text">{journey.stage}</span>
                      </div>
                      <div className="journey-card-market">
                        <span className="market-icon">{getMarketIcon(journey.market)}</span>
                        <span className="market-text">{journey.market}</span>
                      </div>
                    </div>
                    {getStatusBadge(journey)}
                  </div>

                  <div className="journey-card-content">
                    <div className="journey-card-persona">
                      <span className="persona-label">{t('journeyList.persona')}</span>
                      <span className="persona-value">{journey.persona}</span>
                    </div>

                    {progress && (
                      <div className="journey-card-progress">
                        <div className="progress-header">
                          <span className="progress-label">{t('journeyList.progress')}</span>
                          <span className="progress-stats">
                            {t('journeyList.stepsCompleted', { completed: progress.completed, total: progress.total })}
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{ width: `${progress.percentage}%` }}
                          />
                        </div>
                        <span className="progress-percentage">{progress.percentage}%</span>
                      </div>
                    )}

                    <div className="journey-card-meta">
                      <div className="journey-card-date">
                        <span className="date-label">{t('journeyList.created')}</span>
                        <span className="date-value">{formatDate(journey.createdAt)}</span>
                      </div>
                      {journey.resetCount && journey.resetCount > 0 && (
                        <div className="journey-card-resets">
                          <span className="resets-icon">🔄</span>
                          <span className="resets-text">{t('journeyList.resets', { count: journey.resetCount })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="journey-card-footer">
                    <button className="journey-card-view-button">
                      {journey.isActive ? t('journeyList.continueJourney') : t('journeyList.viewJourney')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </BasePageLayout>
  );
};

export default JourneyListPage;
